import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';

import { Api } from 'shared/types';
import { formatOfferSearchData } from '../helpers/formatOfferSearchData';
import { FormattedProps, Offer } from '../types';
import { ADD_HEADERS } from '../../reducer';

type Props = {
  crmManagementActsOfferSearch: Api;
  equipmentId: string | null;
  saleDeviceId: string | null;
  majorProducts: FormattedProps['majorProducts']['products'];
};

const MANAGEMENT_ACT = 'MODIFICATION';

export const useOfferSearch = ({
  crmManagementActsOfferSearch,
  equipmentId,
  saleDeviceId,
  majorProducts
}: Props) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ['offerSearch'],
    queryFn: async () => {
      if (!saleDeviceId || !equipmentId) {
        throw new Error('Missing required parameters');
      }
      const params = new URLSearchParams({
        equipmentId,
        saleDeviceId: saleDeviceId || '',
        managementAct: MANAGEMENT_ACT
      });

      const url = `${crmManagementActsOfferSearch.url}?${params.toString()}`;
      const response = await fetch(url, {
        method: crmManagementActsOfferSearch.method,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const body = await response.json();

        throw new Error(body?.[0]?.detail ?? 'Failed to fetch user data');
      }

      const token = response.headers.get('token');
      const correlationId = response.headers.get('correlationid');

      dispatch({
        type: ADD_HEADERS,
        payload: { token, correlationId }
      });

      return formatOfferSearchData(await response.json());
    },
    select: fetchedData => {
      const minorsOffers: {
        detainedToKeep: Record<string, Offer>;
        toDisplay: Record<string, Offer>;
      } = {
        detainedToKeep: {},
        toDisplay: {}
      };

      const majorOffer:
        | (Offer & {
            engagementUnit?: FormattedProps['majorProducts']['products'][0]['engagementUnit'];
          })
        | null =
        fetchedData.offers.find(offer => offer.major && offer.detained) || null;
      if (!majorOffer) {
        return { majorOffer: null };
      }
      // Find the engagement unit for the major offer
      const product = majorProducts.find(
        (product: { productId?: string }) => product.productId === majorOffer.id
      );
      if (!product) {
        return { majorOffer: null };
      }
      majorOffer.engagementUnit = product?.engagementUnit ?? null;

      fetchedData.offers.forEach(offer => {
        if (offer.minor && !majorOffer.forbiddenOptions.includes(offer.id)) {
          const isContentOrService =
            offer.categoryIndicator?.content === true ||
            offer.categoryIndicator?.service === true;

          if (offer.detained || isContentOrService) {
            if (!isContentOrService) {
              minorsOffers.detainedToKeep[offer.id] = offer;
              return;
            }
            minorsOffers.toDisplay[offer.id] = offer;
          }
          return;
        }
      });

      return {
        contractEndDate: fetchedData.contractEndDate,
        majorOffer: majorOffer as
          | (Offer & {
              engagementUnit: FormattedProps['majorProducts']['products'][0]['engagementUnit'];
            })
          | null,
        minorsOffers,
        operators: fetchedData.operators
      };
    },
    enabled: !!(equipmentId && saleDeviceId)
  });
};
